import React, { useEffect, useState, useCallback } from "react";
import { Modal, Button, Space } from "antd";
import { RefreshCw, Clock } from "lucide-react";
import { RefreshModalContext } from "../contexts/RefreshModalContext";

interface RefreshModalProviderProps {
  children: React.ReactNode;
}

export const RefreshModalProvider: React.FC<RefreshModalProviderProps> = ({
  children,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [snoozeTimeout, setSnoozeTimeout] = useState<
    ReturnType<typeof setTimeout> | null
  >(null);

  const isTurkishScheduledTime = useCallback(() => {
    const now = new Date();
    const turkishTime = new Date(now.getTime() + 3 * 60 * 60 * 1000);
    const hour = turkishTime.getUTCHours();
    const minutes = turkishTime.getUTCMinutes();

    return (hour === 9 && minutes === 0) || (hour === 16 && minutes === 0);
  }, []);

  const shouldShowModal = useCallback(() => {
    if (!isTurkishScheduledTime()) return false;

    const now = new Date();
    const today = now.toDateString();
    const currentHour = new Date(
      now.getTime() + 3 * 60 * 60 * 1000
    ).getUTCHours();

    const lastShownKey = `refreshModal_${today}_${currentHour}`;
    const lastShown = localStorage.getItem(lastShownKey);

    return !lastShown;
  }, [isTurkishScheduledTime]);

  const markModalAsShown = useCallback(() => {
    const now = new Date();
    const today = now.toDateString();
    const currentHour = new Date(
      now.getTime() + 3 * 60 * 60 * 1000
    ).getUTCHours();

    const lastShownKey = `refreshModal_${today}_${currentHour}`;
    localStorage.setItem(lastShownKey, "true");
  }, []);

  const setupDailySchedule = useCallback(() => {
    const checkTime = () => {
      if (shouldShowModal()) {
        setIsModalVisible(true);
        markModalAsShown();
      }
    };

    const interval = setInterval(checkTime, 60000);

    checkTime();

    return () => clearInterval(interval);
  }, [shouldShowModal, markModalAsShown]);

  const clearSnoozeTimeout = useCallback(() => {
    if (snoozeTimeout) {
      clearTimeout(snoozeTimeout);
      setSnoozeTimeout(null);
    }
  }, [snoozeTimeout]);

  const showModal = useCallback(() => {
    setIsModalVisible(true);
  }, []);

  const hideModal = useCallback(() => {
    setIsModalVisible(false);
    clearSnoozeTimeout();

    const timeout = setTimeout(() => {
      setIsModalVisible(true);
    }, 5 * 60 * 1000);

    setSnoozeTimeout(timeout);
  }, [clearSnoozeTimeout]);

  const handleRefresh = useCallback(() => {
    clearSnoozeTimeout();
    window.location.reload();
  }, [clearSnoozeTimeout]);

  const handleOk = useCallback(() => {
    hideModal();
  }, [hideModal]);

  const cleanupOldRecords = useCallback(() => {
    const keys = Object.keys(localStorage);
    const today = new Date().toDateString();

    keys.forEach((key) => {
      if (key.startsWith("refreshModal_") && !key.includes(today)) {
        localStorage.removeItem(key);
      }
    });
  }, []);

  useEffect(() => {
    const cleanup = setupDailySchedule();

    cleanupOldRecords();

    return () => {
      cleanup();
      clearSnoozeTimeout();
    };
  }, [setupDailySchedule, clearSnoozeTimeout, cleanupOldRecords]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && shouldShowModal()) {
        setIsModalVisible(true);
        markModalAsShown();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [shouldShowModal, markModalAsShown]);

  return (
    <RefreshModalContext.Provider value={{ showModal, hideModal }}>
      {children}

      <Modal
        title={
          <Space>
            <Clock size={20} color="#1677ff" />
            <span>Sayfa Yenileme Zamanı</span>
          </Space>
        }
        open={isModalVisible}
        footer={null}
        closable={false}
        centered
        maskClosable={false}
        width={400}
      >
        <div style={{ padding: "16px 0" }}>
          <p
            style={{
              fontSize: "16px",
              marginBottom: "20px",
              textAlign: "center",
            }}
          >
            Lütfen sayfayı yenileyiniz. Sistemimize yeni güncelleme gelmiş
            olabilir.
          </p>

          <Space style={{ width: "100%", justifyContent: "center" }}>
            <Button type="default" onClick={handleOk} size="large">
              Tamam
            </Button>
            <Button
              type="primary"
              icon={<RefreshCw size={16} />}
              onClick={handleRefresh}
              size="large"
            >
              Yenile
            </Button>
          </Space>

          <p
            style={{
              fontSize: "12px",
              color: "#666",
              textAlign: "center",
              marginTop: "16px",
              marginBottom: 0,
            }}
          >
            Tamam'a tıklarsanız 5 dakika sonra tekrar hatırlatılacaksınız.
          </p>
        </div>
      </Modal>
    </RefreshModalContext.Provider>
  );
};
